import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import axios from "axios";

const SHIPPING_LABEL_FORMAT_OPTIONS = [
  { value: "thermal_4_25x2", label: 'Thermal 4.25" × 2" (default)' },
  { value: "thermal_4x2", label: 'Thermal 4" × 2"' },
  { value: "thermal_4x6", label: 'Thermal 4" × 6"' },
  { value: "sheet_2x5", label: "Sheet 2 × 5 grid" },
  { value: "custom", label: "Custom size" },
];

const DEFAULT_SETTINGS = {
  requestDeveloperLabel: "Request developer",
  developerTeamUserIds: [],
  autoAddTeamToAutoStores: false,
  autoSyncEnabled: true,
  shippingLabelSettings: {
    defaultFormat: "thermal_4_25x2",
    labelsPerPreviewPage: 30,
    largeRunConfirmationThreshold: 300,
    customLabelWidthIn: 4.25,
    customLabelHeightIn: 2,
    labelLogoUrl: null,
  },
  shippingIntegrationSettings: {
    shippingProviderDefault: "core",
    shipstationEnabled: false,
    allowProviderOverride: false,
  },
  reportBrandingSettings: {
    logoUrl: null,
  },
  lastAutoSyncAt: null,
  lastAutoSyncAttemptAt: null,
  lastAutoSyncError: null,
};

const PromoStoreSettingsPanel = ({ instanceUniqueId }) => {
  const [instanceId, setInstanceId] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [teamUsers, setTeamUsers] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [nextSyncInMinutes, setNextSyncInMinutes] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = useMemo(
    () => ({ headers: { accessToken: localStorage.getItem("accessToken") } }),
    []
  );

  const fetchInstanceId = useCallback(async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  }, [SERVER, headers, instanceUniqueId]);

  const fetchSettings = useCallback(
    async (targetInstanceId = instanceId) => {
      if (!targetInstanceId) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${SERVER}/instances/${targetInstanceId}/promostore-settings`,
          headers
        );
        setSettings({
          ...DEFAULT_SETTINGS,
          ...(res.data?.settings || {}),
        });
        setTeamUsers(res.data?.teamUsers || []);
      } finally {
        setLoading(false);
      }
    },
    [SERVER, headers, instanceId]
  );

  const fetchEligibleUsers = useCallback(
    async (targetInstanceId = instanceId) => {
      if (!targetInstanceId) return;
      try {
        const roles = ["administrator", "promostores-manager"];
        const requests = roles.map((role) =>
          axios.get(`${SERVER}/auth/users/picker`, {
            params: {
              instanceId: targetInstanceId,
              role,
              pageSize: 200,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          })
        );
        const responses = await Promise.all(requests);
        const combined = responses.flatMap((res) => res.data?.users || []);
        const unique = new Map();
        combined.forEach((user) => {
          if (user?.id != null) unique.set(user.id, user);
        });
        setEligibleUsers(Array.from(unique.values()));
      } catch (error) {
        console.error("Failed to load PromoStore developer candidates:", error);
      }
    },
    [SERVER, instanceId]
  );

  useEffect(() => {
    fetchInstanceId();
  }, [fetchInstanceId]);

  useEffect(() => {
    if (!instanceId) return;
    fetchSettings(instanceId);
    fetchEligibleUsers(instanceId);
  }, [fetchEligibleUsers, fetchSettings, instanceId]);

  useEffect(() => {
    const calculateNextSync = () => {
      const now = new Date();
      const next = new Date(now);
      next.setSeconds(0, 0);
      if (now.getMinutes() < 10) {
        next.setMinutes(10);
      } else {
        next.setHours(now.getHours() + 1);
        next.setMinutes(10);
      }
      const diffMs = next.getTime() - now.getTime();
      const minutes = Math.max(0, Math.ceil(diffMs / 60000));
      setNextSyncInMinutes(minutes);
    };

    calculateNextSync();
    const timer = setInterval(calculateNextSync, 60000);
    return () => clearInterval(timer);
  }, []);


  const handleLogoPicked = ({ event, settingsKey, logoKey, label }) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      alert(`Please choose an image file for the ${label}.`);
      if (event?.target) event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader?.result || "");
      setSettings((prev) => ({
        ...prev,
        [settingsKey]: {
          ...(prev[settingsKey] || {}),
          [logoKey]: dataUrl || null,
        },
      }));
    };
    reader.onerror = () => {
      alert("Failed to read the selected logo image.");
    };
    reader.readAsDataURL(file);

    if (event?.target) event.target.value = "";
  };

  const handleLabelLogoPicked = (event) =>
    handleLogoPicked({
      event,
      settingsKey: "shippingLabelSettings",
      logoKey: "labelLogoUrl",
      label: "shipping label logo",
    });

  const handleReportLogoPicked = (event) =>
    handleLogoPicked({
      event,
      settingsKey: "reportBrandingSettings",
      logoKey: "logoUrl",
      label: "order report logo",
    });

  const handleSave = async () => {
    if (!instanceId) return;
    setSaving(true);
    try {
      const payload = {
        requestDeveloperLabel: settings.requestDeveloperLabel,
        developerTeamUserIds: settings.developerTeamUserIds,
        autoAddTeamToAutoStores: settings.autoAddTeamToAutoStores,
        autoSyncEnabled: settings.autoSyncEnabled,
        shippingLabelSettings: settings.shippingLabelSettings,
        shippingIntegrationSettings: settings.shippingIntegrationSettings,
        reportBrandingSettings: settings.reportBrandingSettings,
      };
      const res = await axios.put(
        `${SERVER}/instances/${instanceId}/promostore-settings`,
        payload,
        headers
      );
      setSettings({
        ...DEFAULT_SETTINGS,
        ...(res.data?.settings || {}),
      });
      setTeamUsers(res.data?.teamUsers || []);
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async () => {
    if (!instanceId) return;
    setSyncing(true);
    try {
      const res = await axios.post(
        `${SERVER}/instances/${instanceId}/promostore-sync`,
        {},
        headers
      );
      setSettings({
        ...DEFAULT_SETTINGS,
        ...(res.data?.settings || {}),
      });
    } catch (error) {
      console.error("Failed to sync PromoStore orders:", error);
    } finally {
      setSyncing(false);
    }
  };

  const selectedUsers = useMemo(() => {
    if (!settings.developerTeamUserIds.length) return [];
    const eligibleMap = new Map(eligibleUsers.map((user) => [user.id, user]));
    const teamMap = new Map(teamUsers.map((user) => [user.id, user]));
    return settings.developerTeamUserIds
      .map((id) => eligibleMap.get(id) || teamMap.get(id))
      .filter(Boolean);
  }, [eligibleUsers, settings.developerTeamUserIds, teamUsers]);

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          PromoStores
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Configure PromoStore developer requests and auto-assignments.
        </Typography>
      </Box>

      <TextField
        label="Request developer button text"
        value={settings.requestDeveloperLabel}
        onChange={(event) =>
          setSettings((prev) => ({
            ...prev,
            requestDeveloperLabel: event.target.value,
          }))
        }
        helperText="Controls the label shown on the PromoStore request button."
      />

      <Autocomplete
        multiple
        loading={loading}
        options={eligibleUsers}
        value={selectedUsers}
        onChange={(_event, value) =>
          setSettings((prev) => ({
            ...prev,
            developerTeamUserIds: value.map((user) => user.id),
          }))
        }
        getOptionLabel={(option) =>
          `${option.first_name || ""} ${option.last_name || ""}`.trim() ||
          option.email ||
          "User"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Featured developers"
            placeholder="Select PromoStore developers"
            helperText="Eligible roles: Administrators and Web Store Managers."
          />
        )}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={settings.autoAddTeamToAutoStores}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                autoAddTeamToAutoStores: event.target.checked,
              }))
            }
          />
        }
        label="Auto-add the featured developers to PromoStores created automatically."
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={settings.autoSyncEnabled}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                autoSyncEnabled: event.target.checked,
              }))
            }
          />
        }
        label="Auto-sync PromoStore orders every hour."
      />

      <Box sx={{ pt: 1, borderTop: "1px solid #eee", display: "grid", gap: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Shipping Provider Routing
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Controls which shipping provider is used when generating PromoStore labels. ShipStation API keys are managed in Global Settings → Integrations.
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel id="shipping-provider-default">Default shipping provider</InputLabel>
          <Select
            labelId="shipping-provider-default"
            label="Default shipping provider"
            value={settings.shippingIntegrationSettings?.shippingProviderDefault || "core"}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                shippingIntegrationSettings: {
                  ...prev.shippingIntegrationSettings,
                  shippingProviderDefault: event.target.value,
                },
              }))
            }
          >
            <MenuItem value="core">Core (existing labels)</MenuItem>
            <MenuItem value="shipstation">ShipStation</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={settings.shippingIntegrationSettings?.shipstationEnabled === true}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  shippingIntegrationSettings: {
                    ...prev.shippingIntegrationSettings,
                    shipstationEnabled: event.target.checked,
                  },
                }))
              }
            />
          }
          label="Enable ShipStation provider"
        />


        <FormControlLabel
          control={
            <Checkbox
              checked={settings.shippingIntegrationSettings?.allowProviderOverride === true}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  shippingIntegrationSettings: {
                    ...prev.shippingIntegrationSettings,
                    allowProviderOverride: event.target.checked,
                  },
                }))
              }
            />
          }
          label="Allow administrator per-job provider override"
        />
      </Box>

      <Box sx={{ pt: 1, borderTop: "1px solid #eee", display: "grid", gap: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Shipping Label Defaults
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Configure the default label output used by PromoStore order label previews/printing for this instance.
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel id="shipping-label-format">Default label format</InputLabel>
          <Select
            labelId="shipping-label-format"
            label="Default label format"
            value={settings.shippingLabelSettings?.defaultFormat || "thermal_4_25x2"}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                shippingLabelSettings: {
                  ...prev.shippingLabelSettings,
                  defaultFormat: event.target.value,
                },
              }))
            }
          >
            {SHIPPING_LABEL_FORMAT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <TextField
            size="small"
            type="number"
            label="Labels per preview page"
            inputProps={{ min: 10, max: 200 }}
            value={settings.shippingLabelSettings?.labelsPerPreviewPage ?? 30}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                shippingLabelSettings: {
                  ...prev.shippingLabelSettings,
                  labelsPerPreviewPage: Number(event.target.value) || 30,
                },
              }))
            }
          />
          <TextField
            size="small"
            type="number"
            label="Large-run confirmation threshold"
            inputProps={{ min: 25, max: 5000 }}
            value={settings.shippingLabelSettings?.largeRunConfirmationThreshold ?? 300}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                shippingLabelSettings: {
                  ...prev.shippingLabelSettings,
                  largeRunConfirmationThreshold: Number(event.target.value) || 300,
                },
              }))
            }
          />
        </Box>


        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Shipping label logo (top-left)
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <Button variant="outlined" component="label" size="small">
              Upload Logo
              <input hidden type="file" accept="image/*" onChange={handleLabelLogoPicked} />
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() =>
                setSettings((prev) => ({
                  ...prev,
                  shippingLabelSettings: {
                    ...prev.shippingLabelSettings,
                    labelLogoUrl: null,
                  },
                }))
              }
              disabled={!settings.shippingLabelSettings?.labelLogoUrl}
            >
              Clear Logo
            </Button>
            <Typography variant="caption" color="text.secondary">
              Appears on shipping label preview + print output.
            </Typography>
          </Stack>
          {settings.shippingLabelSettings?.labelLogoUrl && (
            <Box
              component="img"
              src={settings.shippingLabelSettings.labelLogoUrl}
              alt="Shipping label logo preview"
              sx={{ width: 180, maxHeight: 56, objectFit: "contain", border: "1px solid #e0e0e0", borderRadius: 1, p: 0.5, backgroundColor: "#fff" }}
            />
          )}
        </Box>

        {(settings.shippingLabelSettings?.defaultFormat || "") === "custom" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField
              size="small"
              type="number"
              label="Custom width (inches)"
              value={settings.shippingLabelSettings?.customLabelWidthIn ?? 4.25}
              inputProps={{ min: 1, max: 8.5, step: 0.01 }}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  shippingLabelSettings: {
                    ...prev.shippingLabelSettings,
                    customLabelWidthIn: Number(event.target.value) || 4.25,
                  },
                }))
              }
            />
            <TextField
              size="small"
              type="number"
              label="Custom height (inches)"
              value={settings.shippingLabelSettings?.customLabelHeightIn ?? 2}
              inputProps={{ min: 1, max: 11, step: 0.01 }}
              onChange={(event) =>
                setSettings((prev) => ({
                  ...prev,
                  shippingLabelSettings: {
                    ...prev.shippingLabelSettings,
                    customLabelHeightIn: Number(event.target.value) || 2,
                  },
                }))
              }
            />
          </Box>
        )}
      </Box>

      <Box sx={{ pt: 1, borderTop: "1px solid #eee", display: "grid", gap: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Order Report Branding
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Configure the branding displayed on generated PromoStore order reports.
        </Typography>

        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Order report logo
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <Button variant="outlined" component="label" size="small">
              Upload Logo
              <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={handleReportLogoPicked} />
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() =>
                setSettings((prev) => ({
                  ...prev,
                  reportBrandingSettings: {
                    ...(prev.reportBrandingSettings || {}),
                    logoUrl: null,
                  },
                }))
              }
              disabled={!settings.reportBrandingSettings?.logoUrl}
            >
              Clear Logo
            </Button>
            <Typography variant="caption" color="text.secondary">
              Appears in the header of generated PromoStore order reports.
            </Typography>
          </Stack>
          {settings.reportBrandingSettings?.logoUrl && (
            <Box
              component="img"
              src={settings.reportBrandingSettings.logoUrl}
              alt="Order report logo preview"
              sx={{ width: 180, maxHeight: 70, objectFit: "contain", border: "1px solid #e0e0e0", borderRadius: 1, p: 0.75, backgroundColor: "#fff" }}
            />
          )}
        </Box>
      </Box>

      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        Last auto sync on:{" "}
        {settings.lastAutoSyncAt
          ? new Date(settings.lastAutoSyncAt).toLocaleString()
          : "Not yet"}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        Last auto sync attempt:{" "}
        {settings.lastAutoSyncAttemptAt
          ? new Date(settings.lastAutoSyncAttemptAt).toLocaleString()
          : "Not yet"}
      </Typography>
      {settings.lastAutoSyncError && (
        <Typography variant="body2" sx={{ color: "warning.main" }}>
          Last auto sync error: {settings.lastAutoSyncError}
        </Typography>
      )}
      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        Next auto sync in:{" "}
        {nextSyncInMinutes === null ? "—" : `${nextSyncInMinutes} minute${nextSyncInMinutes === 1 ? "" : "s"}`}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save PromoStore settings"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleManualSync}
          disabled={syncing}
        >
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
        {teamUsers.length > 0 && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {teamUsers.length} developer{teamUsers.length === 1 ? "" : "s"} configured.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PromoStoreSettingsPanel;
